import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { STORE_ID } from "../Config/Constants";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { ROLE, isHead, isRole, isServiceHead } from "../utils/roles";
import BranchChip from "./BranchChip";
import axios from "axios";

/* ----------------------------------------------------------------------------
 * TICKET NUMBERS ARE NOT UNIQUE
 *
 * A number is <store initial>-<branch initial>-<n>, so K-K-100 exists in every
 * store whose name starts with K — and 101 of the 354 stores start with S, with
 * two of them called KUMUDU outright. On Kumudu's queue, which spans every
 * store, searching a number can therefore return several genuinely different
 * tickets that print identically.
 *
 * So every result here names its store, and its branch where the branch says
 * something (BranchChip drops itself otherwise). Without that, the search
 * silently answers a different question from the one being asked.
 * -------------------------------------------------------------------------- */

const BLUE = "#2563EB";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";

const STATUS_TINT = {
  resolved: { bg: "#ECFDF5", fg: "#047857", br: "#A7F3D0" },
  recurring: { bg: "#EEF2FF", fg: "#4338CA", br: "#C7D2FE" },
  follow_up: { bg: "#FFF7ED", fg: "#C2410C", br: "#FED7AA" },
};

const statusTint = (status) =>
  STATUS_TINT[status] || { bg: "#F1F5F9", fg: "#334155", br: BORDER };

const formatWhen = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      });
};

function SearchAnyTicket() {
  // Context
  const {
    userInfo,
    userGoals,
    allKumuduDepartments,
    allInternalDepartments,
    currentStoreInfo,
  } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;
  const role = userInfo?.role;

  const [allTickets, setAllTickets] = useState(null); // Stores all fetched tickets
  const [loading, setLoading] = useState(true); // Loader state
  const [error, setError] = useState(false); // Error state
  const [searchForm, setSearchForm] = useState("");

  // Fetch tickets based on user role & store
  useEffect(() => {
    const handleFetchTickets = async () => {
      if (store_id === undefined || store_id === null) {
        console.log("Skipping API call — store_id not ready yet");
        return;
      }
      setLoading(true);
      try {
        let url;

        // Different endpoints based on store and role
        if (store_id === STORE_ID) {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getAllTickets`;
          if (isServiceHead(role) || isRole(role, ROLE.SERVICE_STAFF)) {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getAllTickets`;
          }
        } else {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getTicketsByStore/${store_id}`;
        }

        const res = await axios.get(url);
        const allTickets = res?.data?.data || [];
        const allTicketsPerDepartment =
          allTickets.filter((ticket) => ticket.dept_id === userInfo.dept_id) ||
          [];
        isHead(userInfo.role)
          ? setAllTickets(allTickets)
          : setAllTickets(allTicketsPerDepartment);
      } catch (error) {
        console.error("API error:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    handleFetchTickets();
    // eslint-disable-next-line
  }, [store_id]);

  // Filter tickets.
  //
  // Guarded on allTickets because the field accepts typing straight away while
  // the fetch is still in flight, and the previous version read .filter off the
  // null it starts as.
  const query = searchForm.trim().toLowerCase();
  const filteredTickets =
    query === "" || !Array.isArray(allTickets)
      ? []
      : allTickets.filter((ticket) => {
          const number = String(ticket?.ticket_number ?? "").toLowerCase();
          // The store name is searchable as well as the number, because the
          // number alone cannot separate two stores' K-K-100 — and somebody
          // looking at a duplicate needs a way to narrow it.
          const store = String(ticket?.store_name ?? "").toLowerCase();
          return number.includes(query) || store.includes(query);
        });

  // How many branches OUR store has, for suppressing a branch chip that would
  // only repeat its store's name. Unknown for every other store's rows, which
  // is the safe way round — see BranchChip.
  const ownBranchCount = currentStoreInfo?.branch_count;

  return (
    <>
      {/* Search Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          mt: 1,
        }}
      >
        <TextField
          fullWidth
          placeholder="Ticket number"
          variant="outlined"
          value={searchForm}
          onChange={(e) => setSearchForm(e.target.value)}
          multiline
          minRows={1}
          maxRows={4}
          sx={formStyle}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Search Form is Empty */}
      {/* {searchForm === "" && (
        <Typography
          sx={{
            textAlign: "center",
            color: "rgba(193,193,193)",
            fontStyle: "italic",
            mt: 1.5,
          }}
        >
          Enter a ticket number above to search
        </Typography>
      )} */}

      {/* Search Results */}
      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress size={22} sx={{ color: BLUE }} />
        </Box>
      )}

      {/* Fetch failed */}
      {error && !loading && (
        <Typography
          sx={{
            textAlign: "center",
            color: "#B91C1C",
            fontSize: 13,
            mt: 1.5,
          }}
        >
          Could not load tickets. Pull down or reopen the screen to try again.
        </Typography>
      )}

      {/* No Tickets Found */}
      {!loading && searchForm !== "" && filteredTickets.length === 0 && (
        <Typography
          sx={{
            textAlign: "center",
            color: "rgba(193,193,193)",
            fontStyle: "italic",
            mt: 1.5,
          }}
        >
          No tickets found
        </Typography>
      )}

      {/* Filtered Tickets */}
      {filteredTickets.length > 0 && (
        <Box sx={{ width: "100%", mt: 0.5 }}>
          <Typography
            sx={{ fontSize: 11, color: INK_MUTED, mb: 0.75, fontWeight: 600 }}
          >
            {filteredTickets.length} match
            {filteredTickets.length === 1 ? "" : "es"}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {filteredTickets.map((ticket) => {
              const tint = statusTint(ticket.status);
              const isOwnStore =
                Number(ticket.store_id) === Number(currentStoreInfo?.id);

              // getAllTickets joins the store name; getTicketsByStore does not,
              // because it was written for a screen that already knew whose
              // store it was showing. This screen does not, so the session's
              // store stands in — and it is the right stand-in, since that
              // endpoint only ever returns rows from the store we are in.
              //
              // This matters for more than the label: BranchChip suppresses a
              // branch that merely repeats its store's name, and it cannot do
              // that if it is never told the name. 258 of the 338 branches are
              // named after their store, so getting this wrong puts the noise
              // back on three quarters of the rows.
              const rowStoreName =
                ticket.store_name ||
                (isOwnStore ? currentStoreInfo?.name : null);

              return (
                <Box
                  key={ticket.id}
                  sx={{
                    border: "1px solid " + BORDER,
                    borderRadius: "10px",
                    bgcolor: "#fff",
                    px: 1.25,
                    py: 1,
                  }}
                >
                  {/* Number, then WHO it belongs to. The store is on the same
                      line as the number and never below it: the two are one
                      identity, and a number read without its store is the
                      ambiguity this screen exists to resolve. */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: INK,
                        fontFamily: "monospace",
                      }}
                    >
                      #{ticket.ticket_number}
                    </Typography>

                    <Chip
                      size="small"
                      icon={<StorefrontRoundedIcon sx={{ fontSize: 11 }} />}
                      label={`${ticket.store_id} · ${
                        rowStoreName || "Unnamed store"
                      }`}
                      sx={{
                        height: 19,
                        fontSize: 10.5,
                        fontWeight: 600,
                        bgcolor: isOwnStore ? "#F1F5F9" : "#EEF2FB",
                        color: isOwnStore ? INK_SOFT : "#1E40AF",
                        border:
                          "1px solid " + (isOwnStore ? BORDER : "#BFDBFE"),
                        "& .MuiChip-icon": { ml: 0.6, color: "inherit" },
                        "& .MuiChip-label": { px: 0.7 },
                      }}
                    />

                    {/* Where in that store it was raised. Silent unless the
                        branch is actually telling you something. */}
                    <BranchChip
                      row={ticket}
                      storeName={rowStoreName}
                      branchCount={isOwnStore ? ownBranchCount : undefined}
                      dense
                    />

                    <Box sx={{ flex: 1 }} />

                    {ticket.status && (
                      <Chip
                        size="small"
                        label={String(ticket.status).replace("_", " ")}
                        sx={{
                          height: 19,
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "capitalize",
                          bgcolor: tint.bg,
                          color: tint.fg,
                          border: "1px solid " + tint.br,
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: INK_SOFT,
                      mt: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {ticket.description || "No description"}
                  </Typography>

                  <Typography sx={{ fontSize: 11, color: INK_MUTED, mt: 0.35 }}>
                    {[
                      ticket.department_name,
                      ticket.created_by_name,
                      formatWhen(ticket.created_at),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
}

const formStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 5,
    height: "44px",
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
      borderRadius: 5,
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(193,193,193)",
  },
};

export default SearchAnyTicket;
